import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Calendar, Trophy, Users, ChevronDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { GameCard } from '@/components/common/GameCard';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';

export const FindGamePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Filter states
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('all');
  
  // Filtered options based on selections
  const [filteredLeagues, setFilteredLeagues] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const pendingUrlFilter = useRef(true);

  // Generate season options (current year and past 3 years)
  const currentYear = new Date().getFullYear();
  const seasons = [
    { value: 'all', label: 'All Seasons' },
    { value: `${currentYear}`, label: `${currentYear}` },
    { value: `${currentYear - 1}`, label: `${currentYear - 1}` },
    { value: `${currentYear - 2}`, label: `${currentYear - 2}` },
    { value: `${currentYear - 3}`, label: `${currentYear - 3}` },
  ];

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [sportsRes, leaguesRes, teamsRes] = await Promise.all([
          api.getSports(),
          api.getLeagues(),
          api.getTeams()
        ]);
        setSports(sportsRes.data);
        setLeagues(leaguesRes.data);
        setFilteredLeagues(leaguesRes.data);
        setTeams(teamsRes.data);
        setFilteredTeams(teamsRes.data);
      } catch (error) {
        console.error('Error loading filter data:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadData();
  }, []);

  // Apply URL search params after data loads
  useEffect(() => {
    if (initialLoading || !pendingUrlFilter.current) return;
    pendingUrlFilter.current = false;

    const teamParam = searchParams.get('team');
    const leagueParam = searchParams.get('league');
    const sportParam = searchParams.get('sport');

    let didSetFilter = false;

    if (teamParam) {
      // Match by name (case-insensitive)
      const match = teams.find(t => t.name.toLowerCase() === teamParam.toLowerCase());
      if (match) {
        // Also set its sport & league for cascading filters
        const teamLeague = leagues.find(l => l.id === match.league_id);
        if (teamLeague) {
          const teamSport = sports.find(s => s.id === teamLeague.sport_id);
          if (teamSport) setSelectedSport(teamSport.id);
          setSelectedLeague(teamLeague.id);
        }
        setSelectedTeam(match.id);
        didSetFilter = true;
      }
    } else if (leagueParam) {
      const match = leagues.find(l => l.slug === leagueParam || l.id === leagueParam);
      if (match) {
        const leagueSport = sports.find(s => s.id === match.sport_id);
        if (leagueSport) setSelectedSport(leagueSport.id);
        setSelectedLeague(match.id);
        didSetFilter = true;
      }
    } else if (sportParam) {
      const match = sports.find(s => s.slug === sportParam || s.id === sportParam);
      if (match) {
        setSelectedSport(match.id);
        didSetFilter = true;
      }
    }

    if (didSetFilter) {
      // Trigger search after a tick so cascading filters settle
      setTimeout(() => {
        document.getElementById('find-game-search-btn')?.click();
      }, 300);
    }
  }, [initialLoading, teams, leagues, sports, searchParams]);

  // Update filtered leagues when sport changes
  useEffect(() => {
    if (selectedSport && selectedSport !== 'all') {
      const sportLeagues = leagues.filter(l => l.sport_id === selectedSport);
      setFilteredLeagues(sportLeagues);
      if (selectedLeague !== 'all' && !sportLeagues.find(l => l.id === selectedLeague)) {
        setSelectedLeague('all');
      }
    } else {
      setFilteredLeagues(leagues);
    }
  }, [selectedSport, leagues]);

  // Update filtered teams when league changes
  useEffect(() => {
    if (selectedLeague && selectedLeague !== 'all') {
      const leagueTeams = teams.filter(t => t.league_id === selectedLeague);
      setFilteredTeams(leagueTeams);
      if (selectedTeam !== 'all' && !leagueTeams.find(t => t.id === selectedTeam)) {
        setSelectedTeam('all');
      }
    } else if (selectedSport && selectedSport !== 'all') {
      const sportLeagueIds = filteredLeagues.map(l => l.id);
      const sportTeams = teams.filter(t => sportLeagueIds.includes(t.league_id));
      setFilteredTeams(sportTeams);
    } else {
      setFilteredTeams(teams);
    }
  }, [selectedLeague, selectedSport, filteredLeagues, teams]);

  // Search for games
  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedLeague && selectedLeague !== 'all') params.league_id = selectedLeague;
      if (selectedSport && selectedSport !== 'all' && selectedLeague === 'all') params.sport_id = selectedSport;
      if (selectedTeam && selectedTeam !== 'all') params.team_id = selectedTeam;
      
      const res = await api.getGames(params);
      let filteredGames = res.data;
      
      // Only show games with video sources (streams)
      filteredGames = filteredGames.filter(game => 
        game.video_sources && game.video_sources.length > 0
      );
      
      // Filter by season (year) if selected
      if (selectedSeason && selectedSeason !== 'all') {
        filteredGames = filteredGames.filter(game => {
          const gameYear = new Date(game.scheduled_time).getFullYear().toString();
          return gameYear === selectedSeason;
        });
      }
      
      setGames(filteredGames);
    } catch (error) {
      console.error('Error searching games:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const handleClear = () => {
    setSelectedSport('all');
    setSelectedLeague('all');
    setSelectedTeam('all');
    setSelectedSeason('all');
    setGames([]);
  };

  // Get display names for active filters
  const getActiveFilters = () => {
    const filters = [];
    if (selectedSport && selectedSport !== 'all') {
      const sport = sports.find(s => s.id === selectedSport);
      if (sport) filters.push(sport.name);
    }
    if (selectedLeague && selectedLeague !== 'all') {
      const league = leagues.find(l => l.id === selectedLeague);
      if (league) filters.push(league.name);
    }
    if (selectedTeam && selectedTeam !== 'all') {
      const team = teams.find(t => t.id === selectedTeam);
      if (team) filters.push(team.name);
    }
    if (selectedSeason && selectedSeason !== 'all') {
      filters.push(selectedSeason);
    }
    return filters;
  };

  return (
    <div data-testid="find-game-page">
      <PageHeroBanner 
        pageSlug="find-game" 
        fallbackTitle="Find a Stream"
        fallbackSubtitle="Search our archive of match streams and replays"
      />
      
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Search Filters */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <Filter className="w-5 h-5 text-sky-400" />
              <h2 className="text-lg font-semibold text-white">Find Your Stream</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Sport Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Trophy className="w-4 h-4 inline mr-1" />
                  Sport
                </label>
                <Select value={selectedSport} onValueChange={setSelectedSport}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="All Sports" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="all" className="text-gray-300">All Sports</SelectItem>
                    {sports.map(sport => (
                      <SelectItem key={sport.id} value={sport.id} className="text-white">
                        {sport.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Competition/League Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Trophy className="w-4 h-4 inline mr-1" />
                  Competition
                </label>
                <Select value={selectedLeague} onValueChange={setSelectedLeague}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="All Competitions" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                    <SelectItem value="all" className="text-gray-300">All Competitions</SelectItem>
                    {filteredLeagues.map(league => (
                      <SelectItem key={league.id} value={league.id} className="text-white">
                        {league.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Club/Team Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Club
                </label>
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="All Clubs" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                    <SelectItem value="all" className="text-gray-300">All Clubs</SelectItem>
                    {filteredTeams.map(team => (
                      <SelectItem key={team.id} value={team.id} className="text-white">
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Season Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Season
                </label>
                <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="All Seasons" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {seasons.map(season => (
                      <SelectItem key={season.value} value={season.value} className="text-white">
                        {season.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button 
                id="find-game-search-btn"
                onClick={handleSearch}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                disabled={loading || initialLoading}
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Searching...' : 'Search Games'}
              </Button>
              <Button 
                onClick={handleClear}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Clear Filters
              </Button>
            </div>

            {/* Active Filters Display */}
            {getActiveFilters().length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-gray-400 text-sm">Active filters:</span>
                {getActiveFilters().map((filter, i) => (
                  <span key={i} className="bg-sky-500/20 text-sky-300 text-sm px-3 py-1 rounded-full border border-sky-500/30">
                    {filter}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Results Section */}
          {games.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">
                  {games.length} Stream{games.length !== 1 ? 's' : ''} Available
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {games.map(game => (
                  <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} />
                ))}
              </div>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-700" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-gray-800/50 rounded-xl p-12 max-w-md mx-auto border border-gray-700">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">Find a stream to watch</p>
                <p className="text-gray-500 text-sm">
                  Use the filters above to search for match streams and replays by sport, competition, club, or season.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindGamePage;
